import React, { useState } from "react";
import { PageHeader } from "@finitless/shared/components";
import { SUPPORT_CONFIG } from "@finitless/shared/supportConstants";
import { useIntercomContext } from "@finitless/shared/contexts";
import { useUserStore } from "@finitless/shared/stores";
import { usePostHog } from "@finitless/shared/hooks";
import { Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";

const SupportPage = ({ selectedBrand }) => {
  const { t } = useTranslation("support");
  const { showMessage, isEnabled } = useIntercomContext();
  const user = useUserStore((state) => state.user);
  const { trackFeatureAdoption, trackEvent } = usePostHog();
  const [copiedPassword, setCopiedPassword] = useState(false);

  const visibleSupportOptions = SUPPORT_CONFIG.supportOptions.filter(
    (option) => option.action !== "canny" || SUPPORT_CONFIG.canny.enabled,
  );

  const handleCopyPassword = (e, password) => {
    e.stopPropagation();
    navigator.clipboard.writeText(password);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const handleVisitKnowledgeBase = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSendMessage = () => {
    if (isEnabled) {
      showMessage(); // Opens Intercom with new message composer
      trackFeatureAdoption.liveChatOpened({
        user_id: user?.id,
      });
    }
  };

  const handleFeedbackClick = () => {
    trackEvent("canny_feedback_clicked");
  };

  const getLocalizedOptionText = (optionId, field, fallbackText) => {
    const key = `options.${optionId}.${field}`;
    const translated = t(key);
    return translated === key ? fallbackText : translated;
  };

  return (
    <div className="flex h-full">
      {/* Main Content Area */}
      <div className="flex-1">
        <PageHeader
          title={t("header.title")}
          selectedBrand={selectedBrand}
          subtitle={t("header.subtitle")}
        />

        <div className="mt-6 space-y-6">
          {/* Support Options Grid */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {visibleSupportOptions.map((option) => (
              <div key={option.id} className="card p-6">
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="bg-brand-gradient flex h-12 w-12 items-center justify-center rounded-lg">
                      {option.icon === "Lightbulb" ? (
                        <Lightbulb className="h-6 w-6 text-white" />
                      ) : (
                        <svg
                          className="h-6 w-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          {option.icon === "BookOpen" && (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          )}
                          {option.icon === "MessageCircle" && (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          )}
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-white">
                        {getLocalizedOptionText(
                          option.id,
                          "title",
                          option.title,
                        )}
                      </h3>
                      {option.badge ? (
                        <span className="rounded-full border border-indigo-500/20 bg-indigo-500/20 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-indigo-400">
                          {getLocalizedOptionText(
                            option.id,
                            "badge",
                            option.badge,
                          )}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-gray-300">
                      {getLocalizedOptionText(
                        option.id,
                        "description",
                        option.description,
                      )}
                    </p>
                    {option.subtitle ? (
                      <p className="mt-1 text-sm text-gray-400">
                        {getLocalizedOptionText(
                          option.id,
                          "subtitle",
                          option.subtitle,
                        )}
                      </p>
                    ) : null}

                    {/* Password with Copy Button */}
                    {option.password ? (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-sm text-gray-400">
                          {t("labels.password")}:
                        </span>
                        <code
                          onClick={(e) =>
                            handleCopyPassword(e, option.password)
                          }
                          className="cursor-pointer rounded bg-gray-700 px-2 py-1 font-mono text-sm text-white transition-colors hover:bg-gray-600"
                        >
                          {option.password}
                        </code>
                        <button
                          type="button"
                          onClick={(e) =>
                            handleCopyPassword(e, option.password)
                          }
                          className="rounded bg-gray-600 px-2 py-1 text-xs text-white transition-colors hover:bg-gray-500"
                        >
                          {copiedPassword
                            ? t("actions.copied")
                            : t("actions.copy")}
                        </button>
                      </div>
                    ) : null}

                    {/* Action Buttons */}
                    <div className="mt-4">
                      {option.action === "external" ? (
                        <button
                          type="button"
                          onClick={() => handleVisitKnowledgeBase(option.url)}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500"
                        >
                          {t("actions.visitKnowledgeBase")}
                        </button>
                      ) : null}
                      {option.action === "intercom" ? (
                        <button
                          type="button"
                          onClick={handleSendMessage}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500"
                        >
                          {t("actions.sendMessage")}
                        </button>
                      ) : null}
                      {option.action === "canny" ? (
                        <a
                          data-canny-link
                          href={option.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleFeedbackClick}
                          className="inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500"
                        >
                          {t("actions.giveFeedback")}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
